import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaArrowRight, FaNewspaper, FaPhoneAlt } from "react-icons/fa";

import { getSettings } from "../services/settingsServices";
import { getMenuFlat } from "../services/menusServices";
import Reveal from "./../components/ui/Reveal";

const isAbsoluteUrl = (href = "") => /^https?:\/\//i.test(href);

const normalizeHref = (href = "") => {
  if (!href) return "#";
  if (isAbsoluteUrl(href)) return href;
  return href.replace(/^\/pages\//, "/page/");
};

function SmartLink({ href, children, className = "", ...props }) {
  const normalized = normalizeHref(href);

  if (isAbsoluteUrl(normalized)) {
    return (
      <a
        href={normalized}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        {...props}
      >
        {children}
      </a>
    );
  }

  if (normalized.startsWith("#")) {
    return (
      <a href={normalized} className={className} {...props}>
        {children}
      </a>
    );
  }

  return (
    <Link to={normalized} className={className} {...props}>
      {children}
    </Link>
  );
}

function ShortcutCard({ item, index }) {
  const href = item.href || "#";

  const accents = [
    {
      border: "border-l-sky-400",
      card: "from-sky-500 via-blue-600 to-blue-950",
      hoverGlow: "group-hover:shadow-sky-950/45",
    },
    {
      border: "border-l-emerald-400",
      card: "from-emerald-500 via-teal-600 to-teal-950",
      hoverGlow: "group-hover:shadow-emerald-950/45",
    },
    {
      border: "border-l-orange-400",
      card: "from-amber-500 via-orange-600 to-red-800",
      hoverGlow: "group-hover:shadow-orange-950/45",
    },
  ];

  const accent = accents[index % accents.length];

  return (
    <Reveal className="h-full" style={{ transitionDelay: `${index * 90}ms` }}>
      <SmartLink
        href={href}
        className={`group relative flex min-h-[92px] overflow-hidden rounded-[1.35rem] border-l-[10px] ${accent.border} bg-gradient-to-r ${accent.card} text-white shadow-2xl shadow-black/25 transition duration-300 hover:-translate-y-1 hover:scale-[1.01] hover:text-white ${accent.hoverGlow} md:min-h-[75px]`}
      >
        <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/18 via-white/5 to-black/20" />
        <span className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full bg-white/18 blur-2xl transition duration-300 group-hover:scale-125 group-hover:bg-white/25" />
        <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-white/45 to-transparent" />

        <div className="relative flex w-full items-center justify-between gap-4 px-5 py-4 md:px-6">
          <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/80">
              Accès rapide
            </p>

            <h3 className="mt-1 truncate text-xl font-bold leading-tight text-white md:text-2xl">
              {item.label}
            </h3>
          </div>

          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white/18 text-white shadow-lg shadow-black/10 transition duration-300 group-hover:translate-x-1 group-hover:bg-white group-hover:text-slate-950">
            <FaArrowRight className="h-4 w-4" />
          </div>
        </div>
      </SmartLink>
    </Reveal>
  );
}

export default function HeroHeader() {
  const [index, setIndex] = useState(0);
  const [settings, setSettings] = useState(null);
  const [images, setImages] = useState([]);
  const [favoriteMenus, setFavoriteMenus] = useState([]);

  useEffect(() => {
    let mounted = true;

    const fetchSettings = async () => {
      try {
        const data = await getSettings();
        const currentSettings = Array.isArray(data) ? data[0] : data;

        if (!mounted || !currentSettings) return;

        setSettings(currentSettings);

        setImages(
          [
            currentSettings.photoURL1,
            currentSettings.photoURL2,
            currentSettings.photoURL3,
          ].filter((url) => typeof url === "string" && url.trim().length > 0),
        );
      } catch (error) {
        console.error("Erreur getSettings:", error);
      }
    };

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchFavoriteMenus = async () => {
      try {
        const all = await getMenuFlat({
          menu_key: "header",
          locale: "fr",
          include_inactive: false,
        });

        const favorites = (Array.isArray(all) ? all : [])
          .filter(
            (menu) => menu.is_favorite === true && menu.is_active !== false,
          )
          .sort(
            (a, b) =>
              (a.order_index ?? 0) - (b.order_index ?? 0) ||
              String(a.label || "").localeCompare(String(b.label || ""), "fr"),
          )
          .slice(0, 3);

        if (mounted) {
          setFavoriteMenus(favorites);
        }
      } catch (error) {
        console.error("Erreur getMenuFlat favoris Hero:", error);
      }
    };

    fetchFavoriteMenus();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (images.length <= 1) return;

    const timer = window.setInterval(() => {
      setIndex((current) => (current + 1) % images.length);
    }, 6500);

    return () => window.clearInterval(timer);
  }, [images.length]);

  const ville = settings?.nom_mairie || settings?.ville || "Votre mairie";
  const logoMairie = settings?.logo_mairie;
  const primaryColor = settings?.primary_color?.trim() || "#0ea5e9";

  const resolveHref = (menu) => {
    if (menu?.url) return normalizeHref(menu.url);

    if (menu?.page?.slug) {
      const slug = menu.page.slug;

      if (slug === "agenda") return "/agenda";

      return `/page/${slug}`;
    }

    return "#";
  };

  const shortcuts = useMemo(() => {
    if (favoriteMenus.length > 0) {
      return favoriteMenus.map((menu) => ({
        id: menu.id,
        label: menu.label,
        href: resolveHref(menu),
      }));
    }

    return [
      {
        id: "fallback-actualites",
        label: "Actualités",
        href: "/page/actualites",
      },
      {
        id: "fallback-agenda",
        label: "Agenda",
        href: "/agenda",
      },
      {
        id: "fallback-contact",
        label: "Contact",
        href: "#contact",
      },
    ];
  }, [favoriteMenus]);

  const scrollToContact = () => {
    const target =
      document.getElementById("contact") ||
      document.querySelector("[data-contact-section]");

    if (!target) return;

    target.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-slate-950"
      style={{ "--hero-accent": primaryColor }}
    >
      <div className="relative min-h-[560px] overflow-hidden md:min-h-[640px] lg:min-h-[680px]">
        {images.length > 0 ? (
          images.map((src, imageIndex) => (
            <div
              key={`${src}-${imageIndex}`}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
                imageIndex === index ? "opacity-100" : "opacity-0"
              }`}
              aria-hidden={imageIndex !== index}
            >
              <img
                src={src}
                alt=""
                className="h-full w-full scale-105 object-cover"
                loading={imageIndex === 0 ? "eager" : "lazy"}
              />
            </div>
          ))
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-sky-950" />
        )}

        {/* Assombrissement fort et lisible */}
        <div className="absolute inset-0 bg-slate-950/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/92 via-slate-950/72 to-slate-950/42" />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/36 via-transparent to-slate-950/90" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(14,165,233,0.28),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(16,185,129,0.16),transparent_32%)]" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-slate-950 via-slate-950/88 to-transparent" />

        <div className="relative mx-auto flex min-h-[560px] max-w-[1380px] flex-col justify-center px-4 pb-32 pt-14 sm:px-6 md:min-h-[640px] md:pb-36 lg:min-h-[680px] lg:px-8">
          <div className="max-w-5xl">
            <Reveal>
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center bg-black/50 rounded-3xl p-6 shadow-2xl shadow-black/30 backdrop-blur-md md:p-8">
                {logoMairie ? (
                  <div className="relative w-fit shrink-0">
                    <div
                      className="absolute -inset-3 rounded-[2rem] opacity-25 blur-2xl"
                      style={{ backgroundColor: primaryColor }}
                    />

                    <img
                      src={logoMairie}
                      alt={`Logo ${ville}`}
                      className="relative h-32 w-32 rounded-[1.75rem] object-contain p-3 sm:h-32 sm:w-32 md:h-48 md:w-48"
                    />
                  </div>
                ) : (
                  <div
                    className="flex h-24 w-24 shrink-0 items-center justify-center rounded-[1.75rem] text-4xl font-bold text-white shadow-2xl shadow-black/30 sm:h-32 sm:w-32"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {ville.charAt(0)}
                  </div>
                )}

                <div>
                  <h1 className="max-w-4xl text-4xl font-bold leading-tight tracking-tight text-white drop-shadow-[0_12px_36px_rgba(0,0,0,0.65)] md:text-6xl lg:text-7xl">
                    {ville}
                  </h1>

                  <p className="mt-4 max-w-2xl text-base font-bold leading-8 text-white/95 drop-shadow-[0_8px_24px_rgba(0,0,0,0.65)] md:text-xl">
                    Retrouvez les informations pratiques, les actualités, les
                    événements et les services de votre commune.
                  </p>
                </div>
              </div>
            </Reveal>

            <Reveal>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <SmartLink
                  href="/actualites"
                  className="group inline-flex min-h-[52px] items-center justify-center gap-3 overflow-hidden rounded-2xl bg-white px-6 py-3 text-base font-bold text-slate-950 shadow-xl shadow-black/30 transition hover:-translate-y-0.5 hover:bg-slate-100"
                >
                  <FaNewspaper
                    className="h-4 w-4"
                    style={{ color: primaryColor }}
                  />
                  Voir les actualités
                  <FaArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </SmartLink>

                <button
                  type="button"
                  onClick={scrollToContact}
                  className="group inline-flex min-h-[52px] items-center justify-center gap-3 rounded-2xl border border-white/25 bg-black/40 px-6 py-3 text-base font-bold text-white shadow-xl shadow-black/25 backdrop-blur-md transition hover:-translate-y-0.5 hover:bg-black/70"
                >
                  <FaPhoneAlt className="h-4 w-4" />
                  Contacter la mairie
                </button>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 hidden px-6 pb-6 md:block">
          <div className="mx-auto grid max-w-[1380px] grid-cols-3 gap-4">
            {shortcuts.map((shortcut, shortcutIndex) => (
              <ShortcutCard
                key={shortcut.id || `${shortcut.label}-${shortcutIndex}`}
                item={shortcut}
                index={shortcutIndex}
              />
            ))}
          </div>
        </div>

        {images.length > 1 && (
          <div className="absolute bottom-4 right-5 hidden items-center gap-2 rounded-full border border-white/15 bg-black/35 px-3 py-2 backdrop-blur-md md:flex">
            {images.map((_, dotIndex) => (
              <button
                key={dotIndex}
                type="button"
                onClick={() => setIndex(dotIndex)}
                aria-label={`Afficher l’image ${dotIndex + 1}`}
                className={`h-2.5 rounded-full transition-all ${
                  dotIndex === index
                    ? "w-8 bg-white"
                    : "w-2.5 bg-white/45 hover:bg-white/75"
                }`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="relative z-10 bg-slate-50 px-4 pb-4 pt-5 dark:bg-slate-950 md:hidden">
        <div className="space-y-3">
          {shortcuts.map((shortcut, shortcutIndex) => (
            <ShortcutCard
              key={shortcut.id || `${shortcut.label}-${shortcutIndex}`}
              item={shortcut}
              index={shortcutIndex}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
